import { useHandleResponse } from '@/utils/useHandleResponse';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Button, Spin, Tabs } from 'antd';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { setCompany, updateField } from '../../../contextRedux/Company/CompanyModel';
import { setCompanyTier } from '../../../contextRedux/Company/CompanyTierStore';
import { setCompanyGroups } from '../../../contextRedux/CompanyGroup/CompanyGroupStore';
import { setAccountManagers } from '../../../contextRedux/User/AccountManagerStore';
import CompanyService from '../../../services/company/CompanyService';
import CompanyGroupService from '../../../services/companyGroup/CompanyGroupService';
import ConfigurationService from '../../../services/configuration/ConfigurationService';
import UserService from '../../../services/user/UserService';
import WorkPlaceService from '../../../services/workPlace/WorkPlaceService';
import { RootState } from '../../../store';
import './NewCompany.css';
import ClientDetailsTab from './Tabs/ClientDetailsTab/ClientDetailsTab';
import ContactPersonsTab from './Tabs/ContactPersonsTab/ContactPersonsTab';

const NewCompany: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const useQueryParams = () => {
    return new URLSearchParams(useLocation().search);
  };
  const query = useQueryParams();

  const reduxCompany = useSelector((state: RootState) => state.models.company);
  const [selectedTabKey, setSelectedTabKey] = useState<string>('clientDetailsTab');
  const [id, setId] = useState<string | null>(query.get('id'));

  const { message } = App.useApp();
  const handleResponse = useHandleResponse();
  const userRols = 'senior_sales';

  useEffect(() => {
    const idFromUrl = query.get('id');
    setId(idFromUrl ?? '');
  }, [location.search]);

  useEffect(() => {
    if (id) {
      dispatch(updateField({ field: 'id', value: id }));
    } else {
      dispatch(updateField({ field: 'id', value: '' }));
    }
  }, [id, dispatch]);

  const { isFetching: isCompaniesLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: () =>
      CompanyService.search({
        ids: [id!],
        isAccountManagerIncluded: true,
        isGroupIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setCompany(data?.response[0]));
        return data?.response;
      }),
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _companyGroups, isFetching: isCompanyGroupsLoading } = useQuery({
    queryKey: ['companyGroups'],
    queryFn: () =>
      CompanyGroupService.search({
        isActive: true,
      }).then((data) => {
        dispatch(setCompanyGroups(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _tiers, isFetching: isTiersLoading } = useQuery({
    queryKey: ['tiers'],
    queryFn: () =>
      ConfigurationService.searchCompanyRanks({}).then((data) => {
        dispatch(setCompanyTier(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _accountManagers, isFetching: isAccountManagersLoading } = useQuery({
    queryKey: ['accountManagers'],
    queryFn: () =>
      UserService.search({ isActive: true, rols: userRols }).then((data) => {
        dispatch(setAccountManagers(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { mutate: saveCompany, isPending: _isSavingCompany } = useMutation({
    mutationFn: (formData: any) => CompanyService.upsert(id ?? '', formData),
    onSuccess: (data: any) => {
      if (data) {
        if (data?.errors?.length > 0) {
          data?.errors?.forEach((error: any) => {
            message.error(error);
          });
          return;
        }
        handleResponse(data, data?.response?.name);
        if (
          (data?.response?.id != '' || data?.response?.id != null || data?.response?.id != undefined) &&
          (id === '' || id === null || id === undefined)
        ) {
          saveWorkPlace({
            id: '',
            agents: [{ id: '', userId: data?.response?.accountManagerId, workPlaceId: '' }],
            apartament: '',
            apartmentNumber: '',
            companyId: data?.response?.id,
            contactPersons: [],
            block: '',
            country: data?.response?.country,
            details: '',
            locality: data?.response?.locality,
            name: 'Sediu',
            address: reduxCompany.address ?? '-',
            timetable: 'L-V: 08 - 17',
            workPlaceItems: [],
          });
        }
        data?.response && navigate(`/panel/company/edit?id=${data?.response?.id}`);
      }
    },
    onError: (error: any) => {
      message.error(error.message);
    },
  });

  const { mutate: saveWorkPlace, isPending: _isSavingWorkPlace } = useMutation({
    mutationFn: (formData: any) => WorkPlaceService.upsert('', formData),
    onSuccess: (data: any) => {
      if (data) {
        handleResponse(data, 'Sediul');
      }
    },
    onError: (error: any) => {
      message.error(error.message);
    },
  });

  const handleSaveCompany = async () => {
    saveCompany(reduxCompany);
  };

  const tabs = [
    {
      key: 'clientDetailsTab',
      label: 'Detalii client',
      children: <ClientDetailsTab />,
    },
    {
      key: 'contactPersonsTab',
      label: 'Persoane de contact',
      children: <ContactPersonsTab />,
    },
  ];

  if (isCompaniesLoading || isCompanyGroupsLoading || isTiersLoading || isAccountManagersLoading) {
    return <Spin size='large' className='spinner'></Spin>;
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <h2>{id ? reduxCompany?.name : 'Client Nou'}</h2>
        <div>
          <Button onClick={() => handleSaveCompany()}>Salveaza</Button>
        </div>
      </div>
      <Tabs
        activeKey={selectedTabKey}
        onChange={setSelectedTabKey} // Update selected tab on change
        items={tabs}
      />
    </>
  );
};

export default NewCompany;
